import { selectAllSelfAccountIds } from "@somewear/asset";
import { selectActiveIdentityId } from "@somewear/auth";
import type { ConversationPreview } from "@somewear/messaging";
import { createSelector } from "reselect";

import { MediaCommsState } from "../mediaComms.reducer";
import { selectAllMediaCommRooms, selectMediaRoomByRoomName } from "../mediaComms.selectors";
import { MAX_ROOM_TRANSMISSION_CARDS_SHOWN } from "./mediaComms.rooms.model";
import { MediaRoom, MediaRoomTrack } from "./mediaComms.rooms.state";
import { roomMatchesAccountIds } from "./mediaComms.rooms.utils";

type RoomForConversationState = MediaCommsState & Parameters<typeof selectAllSelfAccountIds>[0];

const selectMediaCommRoomEntities = (state: MediaCommsState) => state.mediaCommRooms.entities;

export const selectRoomsWithActiveTracks = createSelector(
	[selectMediaCommRoomEntities],
	(entities) => {
		const rooms = Object.values(entities) as MediaRoom[];
		return rooms.filter((room) => room.tracks.length > 0);
	},
);

const shallowEqualArray = <T>(a: T[], b: T[]) => {
	if (a === b) return true;
	if (a.length !== b.length) return false;
	for (let i = 0; i < a.length; i++) {
		if (a[i] !== b[i]) return false;
	}
	return true;
};

const roomsWithActiveSelfTracksCombiner = (() => {
	let lastResult: MediaRoom[] = [];
	return (activeIdentityId: ReturnType<typeof selectActiveIdentityId>, rooms: MediaRoom[]) => {
		const nextResult = rooms.filter((room) =>
			room.tracks.some(
				(track) => track.participant.identity === activeIdentityId && !track.unpublishedAt,
			),
		);

		if (shallowEqualArray(lastResult, nextResult)) {
			return lastResult;
		}
		lastResult = nextResult;
		return nextResult;
	};
})();

export const selectRoomsWithActiveSelfTracks = createSelector(
	[selectActiveIdentityId, selectRoomsWithActiveTracks],
	roomsWithActiveSelfTracksCombiner,
);

export function selectRoomForConversation(
	state: RoomForConversationState,
	conversation: ConversationPreview,
): MediaRoom | undefined {
	if (conversation.workspaceId) {
		return selectMediaRoomByRoomName(state, conversation.workspaceId);
	}
	const selfAccountIds = selectAllSelfAccountIds(state);
	const allRooms = selectAllMediaCommRooms(state);

	return allRooms.find(
		(r) =>
			r.type === "direct" &&
			conversation.participantId != null &&
			selfAccountIds.some((selfId) =>
				roomMatchesAccountIds(r.name, [selfId, conversation.participantId!]),
			),
	);
}

export function selectVisibleTransmissionRooms(
	state: RoomForConversationState,
	conversations: ConversationPreview[],
): MediaRoom[] {
	return conversations
		.map((c) => selectRoomForConversation(state, c))
		.filter(
			(room): room is MediaRoom =>
				room != null &&
				!room.isMuted &&
				(room.lastActivity ?? 0) > 0 &&
				room.minimizedSnapshot === null &&
				!(
					room.tracks.length === 1 &&
					room.tracks[0].isLocalTrack &&
					!room.tracks[0].isReplyTrack
				),
		)
		.slice(0, MAX_ROOM_TRANSMISSION_CARDS_SHOWN)
		.filter((room) => room.tracks.length > 0);
}

/**
 * Returns only the live remote tracks for a given conversation — remote participants
 * whose tracks have not yet been unpublished. Intended for use with a SID-based
 * equality function so consumers only re-render when participants join or leave,
 * not on every updateRoomTracks dispatch.
 */
export function selectLiveRemoteTracksForConversation(
	state: RoomForConversationState,
	conversation: ConversationPreview,
	activeIdentityId: string | undefined,
): MediaRoomTrack[] {
	const room = selectRoomForConversation(state, conversation);
	if (!room?.tracks.length) return [];
	return room.tracks.filter(
		(t) => t.participant.identity !== activeIdentityId && t.unpublishedAt === undefined,
	);
}
